import { createPitchTracker } from './pitchTracker';
import { sungFrames, judgeSung, hzToMidi } from './pitchGrading';

/**
 * The pitch monitor's engine, kept free of React and the browser so it can
 * be tested: mic samples in, readings and judged notes out. It uses the
 * same pitch reading, "is this singing" rules and note judgement as graded
 * takes. Each held note, once it ends (a gap, or a move to another note),
 * is judged against getTarget() (a MIDI number) or, when that is null, the
 * nearest note to where it was held, and handed to onNote.
 */

// How much history is kept and re-judged
const KEEP_SECONDS = 12;
const VOICING_WINDOW_SECONDS = 10;
// A held note ends after this much not-singing, or when the voice moves at
// least JUMP_SEMITONES away and stays there for JUMP_SECONDS
const GAP_SECONDS = 0.08;
const JUMP_SEMITONES = 0.75;
const JUMP_SECONDS = 0.08;
// Shorter runs are not judged as notes (a blip, the burst at the start of
// a word); a move to another pitch only ends a note that was itself held
// at least HELD_BEFORE_JUMP_SECONDS (anything shorter is the way in)
const SHORTEST_NOTE_SECONDS = 0.2;
const HELD_BEFORE_JUMP_SECONDS = 0.25;
// ...held, meaning its middle half stays within this (vibrato included)
const HELD_SPREAD_SEMITONES = 0.6;

const median = (values) => [...values].sort((a, b) => a - b)[values.length >> 1];

export function createMonitorEngine({ sampleRate, getTarget, onNote }) {
  const tracker = createPitchTracker(sampleRate);
  const hopSeconds = tracker.hopSeconds;
  // [{ time, midi, f0, clarity, rms, sung }] in order
  const buffer = [];
  // The held note being sung: index into buffer (or -1), frames scanned so
  // far, the quiet run since, and the singing just before (legato)
  const run = { from: -1, scanned: 0, quiet: 0, before: null };

  const judge = (points, before) => {
    if (points.length * hopSeconds < SHORTEST_NOTE_SECONDS) return;
    const heard = points.map(p => ({ time: p.time, midi: p.midi, clarity: p.clarity }));
    const goal = getTarget();
    const note = { midi: goal === null ? Math.round(median(heard.map(h => h.midi))) : goal };
    const result = judgeSung({ heard, notes: [note], hopSeconds, before, judgeScoop: true });
    onNote({
      from: heard[0].time,
      to: heard[heard.length - 1].time,
      gradedFrom: result.graded[0].time,
      gradedTo: result.graded[result.graded.length - 1].time,
      level: note.midi + result.offset,
      target: note.midi,
      nearest: goal === null,
      cents: result.cents,
      grade: result.grade,
      scoop: result.scoop,
      settle: result.settle
    });
  };

  const push = (samples, frame) => {
    for (const f of tracker.push(samples, frame)) {
      buffer.push({ time: f.sample / sampleRate, midi: hzToMidi(f.f0), f0: f.f0, clarity: f.clarity, rms: f.rms, sung: false });
    }
    if (!buffer.length) return;
    const now = buffer[buffer.length - 1].time;

    // Forget what has scrolled off
    let drop = 0;
    while (drop < buffer.length && buffer[drop].time < now - KEEP_SECONDS) drop++;
    if (drop) {
      buffer.splice(0, drop);
      if (run.from >= 0) run.from = Math.max(0, run.from - drop);
      run.scanned = Math.max(0, run.scanned - drop);
    }

    // Which readings count as singing: the grader's own rules over the
    // recent past (a reading's verdict can change as the singing around it
    // arrives)
    let from = 0;
    while (from < buffer.length && buffer[from].time < now - VOICING_WINDOW_SECONDS) from++;
    const recent = buffer.slice(from);
    const sung = sungFrames(recent, hopSeconds);
    recent.forEach((b, i) => { b.sung = sung[i]; });

    // Held notes: a run of singing ends at a gap or a jump to another note.
    // Only readings old enough for their verdict to have settled are used.
    const span = Math.round(JUMP_SECONDS / hopSeconds);
    const held = Math.round(HELD_BEFORE_JUMP_SECONDS / hopSeconds);
    const settledUpTo = buffer.length - Math.ceil(GAP_SECONDS / hopSeconds);
    for (let i = run.scanned; i < settledUpTo; i++) {
      const b = buffer[i];
      if (run.from < 0) {
        if (b.sung) {
          run.from = i;
          run.quiet = 0;
        }
        continue;
      }
      if (!b.sung) {
        run.quiet++;
        if (run.quiet * hopSeconds >= GAP_SECONDS) {
          judge(buffer.slice(run.from, i - run.quiet + 1).filter(p => p.sung), run.before);
          run.from = -1;
          run.before = null;
        }
        continue;
      }
      run.quiet = 0;
      // A jump: the last JUMP_SECONDS sit well away from a held run before
      // them (a shorter run is the scoop or consonant leading into the note)
      // (the stretch just before the move must itself have been held:
      // compared with the whole run, a burst at the start of a word could
      // outweigh the start of the vowel after it)
      const cut = i - span + 1;
      if (cut - run.from >= held) {
        const latest = buffer.slice(cut, i + 1).filter(p => p.sung).map(p => p.midi);
        const before = buffer.slice(cut - held, cut).filter(p => p.sung).map(p => p.midi).sort((a, b) => a - b);
        const steady = before.length >= held * 0.8
          && before[Math.floor(before.length * 0.75)] - before[Math.floor(before.length * 0.25)] <= HELD_SPREAD_SEMITONES;
        if (latest.length >= span - 1 && steady && Math.abs(median(latest) - median(before)) >= JUMP_SEMITONES) {
          const points = buffer.slice(run.from, cut).filter(p => p.sung);
          judge(points, run.before);
          run.before = points.length ? points[points.length - 1] : null;
          run.from = cut;
        }
      }
    }
    run.scanned = Math.max(run.scanned, settledUpTo);
  };

  return { push, buffer, hopSeconds };
}
